"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/* tslint:disable: variable-name no-shadowed-variable */
var VirialEquationOfState = require("./virial-equation-of-state");
var IAPWS06 = require("./iapws-06");
var IAPWSIF97 = require("./iapws-if97");
var HenrysLawConstantofAir = require("./henrys-law-constant-of-air");
/// <param name="p">Pressure [Pa]</param>
/// <param name="T">Temperature [K]</param>
/// <param name="p_ws">Water Vapor Saturation Or Sublimation Pressure [Pa]</param>
/// <returns>Enhancement Factor [1/1]</returns>
function Get_Enhancement_Factor(p, T, p_ws) {
    if (p <= 0) {
        throw new Error("ArgumentOutOfRangeException: p");
    }
    var Rbar = 8.314472; // [kJ/(kmol*K)] or [J/(mol*K)]
    // βH is set to zero for T ≤ 273.15 K or T > Tw,s(p), where Tw,s is the saturation temperature of pure water calculated from [1] Eq. 2.92.
    var beta_H;
    if (T < 273.15 || T > IAPWSIF97.Get_Saturation_Temperature_of_Water(p)) {
        beta_H = 0.0;
    }
    else {
        beta_H = HenrysLawConstantofAir.Get_Henrys_Law_Constant_of_Air(T, p_ws); // [1/Pa]
    }
    // If pw,s(T) > p, from [1] section 3.4.2.2: “In the iteration process of the enhancement factor in Eq. (3.25), κT is set to zero for pw,s(T) > p.”
    var kappa_T;
    if (p_ws > p) {
        kappa_T = 0.0;
    }
    else {
        if (T >= 273.15) {
            kappa_T = IAPWSIF97.Get_Isothermal_Compressibility_of_Liquid_Water(p, T);
        }
        else {
            kappa_T = IAPWS06.Get_Isothermal_Compressibility_of_Ice(p, T);
        }
    }
    // Get Molar volume
    var vbar_ws;
    if (T >= 273.15) {
        vbar_ws = IAPWSIF97.Get_Molar_volume_of_Liquid_Water(p, T);
    }
    else {
        vbar_ws = IAPWS06.Get_Molar_volume_of_Ice(p, T);
    }
    // Get Virial coeffs
    var Baa = VirialEquationOfState.Get_Baa(T);
    var Baw = VirialEquationOfState.Get_Baw(T);
    var Bww = VirialEquationOfState.Get_Bww(T);
    var Caaa = VirialEquationOfState.Get_Caaa(T);
    var Caaw = VirialEquationOfState.Get_Caaw(T);
    var Caww = VirialEquationOfState.Get_Caww(T);
    var Cwww = VirialEquationOfState.Get_Cwww(T);
    // Use [1] eq. (3.24) and (3.25) to find f and ψw,s by iteration using the secant method. Start with an initial guess of f = 1.
    var x1 = 0;
    var x2 = 0;
    var x3 = 0;
    var y1 = 0;
    var y2 = 0;
    var change = 0;
    var iter_no = 1;
    var f = 1.0;
    while ((iter_no <= 3 || change > 1e-7) && iter_no < 100) {
        if (iter_no === 1) {
            x1 = 1.00;
            f = x1;
        }
        if (iter_no === 2) {
            x2 = 1.00 + 0.000001;
            f = x2;
        }
        if (iter_no > 2) {
            f = x2;
        }
        // Equation (3.24)
        var psi_ws = f * p_ws / p;
        // Left-hand-side of Equation (3.25)
        var LHS = Math.log(f);
        // Right-hand-side of Equation (3.25)
        var line1 = ((1 + kappa_T * p_ws) * (p - p_ws) - 0.5 * kappa_T * (p * p - p_ws * p_ws)) / (Rbar * T) * vbar_ws +
            Math.log(1 - beta_H * (1 - psi_ws) * p);
        var line2 = Math.pow(1 - psi_ws, 2) * p / (Rbar * T) * Baa - 2 * Math.pow(1 - psi_ws, 2) * p / (Rbar * T) * Baw -
            (p - p_ws - Math.pow(1 - psi_ws, 2) * p) / (Rbar * T) * Bww;
        var line3 = Math.pow(1 - psi_ws, 3) * p * p / Math.pow(Rbar * T, 2) * Caaa +
            (3 * Math.pow(1 - psi_ws, 2) * (1 - 2 * (1 - psi_ws)) * p * p) / (2 * Math.pow(Rbar * T, 2)) * Caaw;
        var line4 = -3 * Math.pow(1 - psi_ws, 2) * psi_ws * p * p / Math.pow(Rbar * T, 2) * Caww -
            ((3 - 2 * psi_ws) * psi_ws * psi_ws * p * p - p_ws * p_ws) / (2 * Math.pow(Rbar * T, 2)) * Cwww;
        var line5 = -(Math.pow(1 - psi_ws, 2) * (-2 + 3 * psi_ws) * psi_ws * p * p) / Math.pow(Rbar * T, 2) * Baa * Bww;
        var line6 = -(2 * Math.pow(1 - psi_ws, 3) * (-1 + 3 * psi_ws) * p * p) / Math.pow(Rbar * T, 2) * Baa * Baw;
        var line7 = (6 * Math.pow(1 - psi_ws, 2) * psi_ws * psi_ws * p * p) / Math.pow(Rbar * T, 2) * Bww * Baw -
            (3 * Math.pow(1 - psi_ws, 4) * p * p) / (2 * Math.pow(Rbar * T, 2)) * Baa * Baa;
        var line8 = -(2 * Math.pow(1 - psi_ws, 2) * psi_ws * (-2 + 3 * psi_ws) * p * p) / Math.pow(Rbar * T, 2) * Baw * Baw -
            (p_ws * p_ws - (4 - 3 * psi_ws) * Math.pow(psi_ws, 3) * p * p) / (2 * Math.pow(Rbar * T, 2)) * Bww * Bww;
        var RHS = line1 + line2 + line3 + line4 + line5 + line6 + line7 + line8;
        if (iter_no === 1) {
            y1 = LHS - RHS;
        }
        if (iter_no > 1) {
            y2 = LHS - RHS;
            x3 = x2 - y2 / (y2 - y1) * (x2 - x1);
            change = Math.abs(y2 / (y2 - y1) * (x2 - x1));
            y1 = y2;
            x1 = x2;
            x2 = x3;
        }
        iter_no = iter_no + 1;
    }
    if (f >= 1.0) {
        return f;
    }
    else {
        return 1.0;
    }
}
exports.Get_Enhancement_Factor = Get_Enhancement_Factor;
